import {
  Add,
  CollectionsBookmark,
  InsertChart,
  People,
  PinDrop,
  Today,
  WifiTethering,
  Work,
  Assistant,
  Warehouse,
  Search,
  ManageAccounts,
} from '@mui/icons-material'
import { Drawer, Toolbar, List, Box, Link, Icon } from '@mui/material'
import I18n from 'i18n-js'
import { memo, useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'

import manageBrandsPaths from '@src/components/brands/manageBrandsPaths'
import PendingInvitesChip from '@src/components/brands/PendingInvitesChip'
import calendarPaths from '@src/components/calendar/calendarPaths'
import { getCampaignContentPaths } from '@src/components/content/campaignContentPaths'
import industryContentPaths from '@src/components/content/industryContentPaths'
import organicCampaignPaths from '@src/components/content/organicCampaignPaths'
import downloadablesPaths from '@src/components/downloadables/downloadablesPaths'
import embeddedSearchPaths from '@src/components/embeddedSmartSearch/embeddedSearchPaths'
import leadsPaths from '@src/components/leads/leadPaths'
import reportingPaths from '@src/components/reporting/reportingPaths'
import retailerSettingsPaths from '@src/components/retailerSettings/retailerSettingsPaths'
import ChildSpacer from '@src/lib/ChildSpacer'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'
import { useRetailerAccountSocialAccounts } from '@src/lib/useRetailerAccountSocialAccounts'
import { currentRetailerAccountAndBrandSlugSelector } from '@src/stores/app/selectors'
import { selectIsLocalAdsAvailable } from '@src/stores/mainMenu/selectors'
import { allocationDetailsTotalForCurrentRetailerAccountSelector } from '@src/stores/retailerAccounts/allocationDetails/selectors'
import ReactComponent from '@src/svgs/logo-wide.svg?react'

import SubmitAnIdeaDialog from '../SubmitAnIdeaDialog'

import FundingChip from './FundingChip'
import SideNavListItem from './SideNavListItem'

export const SIDENAV_WIDTH = 250

interface SideNavProps {
  isOpen: boolean
}

const useStyles = makeStyles()((theme) => ({
  paper: {
    width: SIDENAV_WIDTH,
    zIndex: theme.zIndex.appBar - 1,
  },
  navList: {
    overflow: 'auto',
    flexGrow: 1,
  },
  list: {
    '& .Mui-selected .MuiListItemText-primary': {
      fontWeight: 'bold',
    },
  },
}))

const SideNav: React.FC<React.PropsWithChildren<SideNavProps>> = (props) => {
  const { classes } = useStyles()
  const currentSlugs = useSelector(currentRetailerAccountAndBrandSlugSelector)
  const { allocationDetailsTotal }: { allocationDetailsTotal: number } =
    useSelector(allocationDetailsTotalForCurrentRetailerAccountSelector)
  const { isLocalAdsAvailable }: { isLocalAdsAvailable: boolean } = useSelector(
    selectIsLocalAdsAvailable,
  )

  const { hasInvalidSocialAccount } = useRetailerAccountSocialAccounts()

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false)
  const showEmbeddedSearch = useFeatureFlag('embedded-smart-search')
  const campaignContentPaths = getCampaignContentPaths(false) // force false since we don't want legacy paths (post improved brand filtering)
  const useNewBusinessSettings = useFeatureFlag('new-business-settings')

  if (!currentSlugs.retailerAccount) {
    return null
  }

  return (
    <>
      <Drawer
        open={props.isOpen}
        variant="persistent"
        classes={{
          paper: classes.paper,
        }}
      >
        <Toolbar />

        <div className={classes.navList}>
          <List dense className={classes.list}>
            <SideNavListItem
              primaryText={I18n.t('nav.brandContent')}
              href={campaignContentPaths.root.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
                brandSlug: currentSlugs.brand,
              })}
              icon={<CollectionsBookmark />}
              data-testid="sidenav-brandContent"
              exact={false}
              forceActive={() =>
                !!campaignContentPaths.index.match() ||
                !!organicCampaignPaths.details.match()
              }
            />

            <SideNavListItem
              primaryText={I18n.t('nav.industryContent')}
              href={industryContentPaths.root.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
              })}
              data-testid="sidenav-industryContent"
              icon={<Warehouse />}
              exact={false}
            />

            {showEmbeddedSearch && (
              <SideNavListItem
                primaryText={I18n.t('embeddedSearch.title')}
                href={embeddedSearchPaths.root.build({
                  retailerAccountSlug: currentSlugs.retailerAccount,
                })}
                icon={<Search />}
                exact={false}
              />
            )}

            {isLocalAdsAvailable ? (
              <SideNavListItem
                primaryText={I18n.t('nav.localAds')}
                href={`/#/${currentSlugs.retailerAccount}/${currentSlugs.brand}/ads`}
                isLegacyLink
                icon={<WifiTethering />}
                data-testid="sidenav-localAds"
                rightChild={
                  allocationDetailsTotal > 0 ? (
                    <FundingChip
                      size="small"
                      variant="outlined"
                      color="secondary"
                      amountInCents={allocationDetailsTotal}
                    />
                  ) : undefined
                }
              />
            ) : undefined}

            <SideNavListItem
              primaryText={I18n.t('nav.schedule')}
              isLegacyLink={false}
              href={calendarPaths.index.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
                brandSlug: currentSlugs.brand,
              })}
              icon={<Today />}
              data-testid="sidenav-schedule"
            />

            <SideNavListItem
              primaryText={I18n.t('nav.reporting')}
              href={reportingPaths.index.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
              })}
              icon={<InsertChart />}
              data-testid="sidenav-reporting"
            />

            <SideNavListItem
              primaryText={I18n.t('nav.customers')}
              href={leadsPaths.root.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
              })}
              icon={<People />}
              data-testid="sidenav-customers"
            />

            <SideNavListItem
              primaryText={I18n.t('retailerSettings.businessInfo.title')}
              href={
                useNewBusinessSettings
                  ? retailerSettingsPaths.businessInfo.build({
                      retailerAccountSlug: currentSlugs.retailerAccount,
                    })
                  : `/#/${currentSlugs.retailerAccount}/${currentSlugs.brand}/settings?tab=business_info`
              }
              forceActive={() =>
                !!retailerSettingsPaths.index.match() &&
                !retailerSettingsPaths.socialAccounts.match()
              }
              isLegacyLink={!useNewBusinessSettings}
              icon={<PinDrop />}
              data-testid="sidenav-businessInfo"
            />

            <SideNavListItem
              primaryText={I18n.t('retailerSettings.socialAccounts.title')}
              href={
                useNewBusinessSettings
                  ? retailerSettingsPaths.socialAccounts.build({
                      retailerAccountSlug: currentSlugs.retailerAccount,
                    })
                  : `/#/${currentSlugs.retailerAccount}/${currentSlugs.brand}/settings?tab=social_accounts`
              }
              isLegacyLink={!useNewBusinessSettings}
              icon={<ManageAccounts />}
              data-testid="sidenav-socialAccounts"
              slotProps={{
                badge: hasInvalidSocialAccount
                  ? { color: 'error', variant: 'dot' }
                  : undefined,
              }}
            />

            <SideNavListItem
              primaryText={I18n.t('downloadables.downloadables')}
              href={downloadablesPaths.index.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
                brandSlug: currentSlugs.brand,
              })}
              icon={<Work />}
              data-testid="sidenav-downloadables"
            />

            {!useNewBusinessSettings && (
              <SideNavListItem
                primaryText={I18n.t('manageBrands.manageBrands')}
                href={manageBrandsPaths.index.build({
                  retailerAccountSlug: currentSlugs.retailerAccount,
                  brandSlug: currentSlugs.brand,
                })}
                exact
                icon={<Icon className="fas fa-building" />}
                data-testid="sidenav-manageBrands"
                rightChild={<PendingInvitesChip />}
              />
            )}

            <SideNavListItem
              primaryText={
                useNewBusinessSettings
                  ? I18n.t('manageBrands.addMoreBrands')
                  : I18n.t('manageBrands.suggestBrands')
              }
              href={manageBrandsPaths.index.build({
                retailerAccountSlug: currentSlugs.retailerAccount,
                brandSlug: currentSlugs.brand,
                action: useNewBusinessSettings ? 'add' : 'suggest',
              })}
              icon={<Add />}
              data-testid="sidenav-suggestBrands"
            />
          </List>
        </div>

        <List dense className={classes.list}>
          <SideNavListItem
            primaryText={I18n.t('nav.submitAnIdea')}
            isLegacyLink
            icon={<Assistant />}
            data-testid="sidenav-submitAnIdea"
            onClick={() => {
              setIsSubmitDialogOpen(true)
            }}
          />
          <SubmitAnIdeaDialog
            open={isSubmitDialogOpen}
            onClose={() => setIsSubmitDialogOpen(false)}
          />
        </List>

        <Box
          color="text.secondary"
          fontSize="body2.fontSize"
          padding={4}
          boxShadow={4}
        >
          <Box display="flex" alignItems="center" justifyContent="center">
            {I18n.t('nav.poweredBy')}&nbsp;
            <Link
              href="http://promoboxx.com/"
              target="_blank"
              rel="noreferrer"
              color="inherit"
            >
              <ReactComponent height="1.3em" />
            </Link>
          </Box>
          <br />
          <Box display="flex" alignItems="center" justifyContent="center">
            <ChildSpacer>
              <Link href="http://promoboxx.com/terms" color="inherit">
                {I18n.t('nav.terms')}
              </Link>
              <span>·</span>
              <Link href="http://promoboxx.com/privacy" color="inherit">
                {I18n.t('nav.privacy')}
              </Link>
            </ChildSpacer>
          </Box>
        </Box>
      </Drawer>
    </>
  )
}

export default memo(SideNav)
